/**
 * Schema-to-Zod Generator
 * 
 * WHY: The Form Factory pattern requires dynamic form generation from
 * backend schemas. This module converts MetadataIQ schema definitions
 * into Zod validation schemas at runtime.
 * 
 * CRITICAL: This is the most important transformation in the UI.
 * Per the Lead Developer, bugs here won't surface until runtime in the SCIF.
 * Every transformation MUST be thoroughly tested.
 * 
 * @see .docs/architecture/software_iq_architecture_review.md - Expert 1
 */

import { z, ZodTypeAny } from 'zod'
import type { Schema, SchemaField, FieldCondition } from '@/api/core/types'

/**
 * Generates a Zod schema from a MetadataIQ schema definition.
 * 
 * WHY: This enables the "Salesforce pattern" - forms are defined in metadata,
 * not hardcoded in JSX. When compliance rules change, only the backend
 * schema needs updating.
 * 
 * @param schema - MetadataIQ schema definition
 * @returns Zod object schema for form validation
 */
export function generateZodSchema(schema: Schema): z.ZodObject<Record<string, ZodTypeAny>> {
  const shape: Record<string, ZodTypeAny> = {}

  for (const field of schema.fields) {
    shape[field.field_id] = generateFieldSchema(field)
  }

  // Strict mode rejects extra fields per the code quality mandate
  return z.object(shape).strict()
}

/**
 * Generates a Zod schema for a single field.
 * 
 * WHY: Each field type maps to specific Zod validators.
 * The validation rules come from the schema, not hardcoded values.
 */
function generateFieldSchema(field: SchemaField): ZodTypeAny {
  let fieldSchema: ZodTypeAny

  switch (field.field_type) {
    case 'string':
      fieldSchema = generateStringSchema(field)
      break
    case 'number':
      fieldSchema = generateNumberSchema(field)
      break
    case 'boolean':
      fieldSchema = z.boolean()
      break
    case 'enum':
      fieldSchema = generateEnumSchema(field)
      break
    case 'date':
      fieldSchema = z.string().datetime({ message: 'Invalid date format' })
      break
    case 'array':
      fieldSchema = z.array(z.string())
      break
    default:
      // Fallback to string for unknown types
      fieldSchema = z.string()
  }

  // Apply optional/required based on field definition
  // WHY: The required_if logic is handled at form level via superRefine
  if (!field.required) {
    fieldSchema = fieldSchema.optional()
  }

  return fieldSchema
}

/**
 * Generates Zod schema for string fields with validation rules.
 */
function generateStringSchema(field: SchemaField): z.ZodString {
  let schema = z.string()

  if (field.validation) {
    const { pattern, min_length, max_length } = field.validation

    if (min_length !== undefined) {
      schema = schema.min(min_length, {
        message: `${field.display_name} must be at least ${min_length} characters`,
      })
    }

    if (max_length !== undefined) {
      schema = schema.max(max_length, {
        message: `${field.display_name} must be at most ${max_length} characters`,
      })
    }

    if (pattern) {
      schema = schema.regex(new RegExp(pattern), {
        message: `${field.display_name} format is invalid`,
      })
    }
  }

  return schema
}

/**
 * Generates Zod schema for number fields with validation rules.
 */
function generateNumberSchema(field: SchemaField): z.ZodNumber {
  let schema = z.number()

  if (field.validation) {
    const { minimum, maximum } = field.validation

    if (minimum !== undefined) {
      schema = schema.min(minimum, {
        message: `${field.display_name} must be at least ${minimum}`,
      })
    }

    if (maximum !== undefined) {
      schema = schema.max(maximum, {
        message: `${field.display_name} must be at most ${maximum}`,
      })
    }
  }

  return schema
}

/**
 * Generates Zod schema for enum fields.
 * WHY: Enum values come from the schema, not hardcoded arrays.
 */
function generateEnumSchema(field: SchemaField): ZodTypeAny {
  const enumValues = field.validation?.enum

  if (!enumValues || enumValues.length === 0) {
    // Fallback if no enum values defined
    return z.string()
  }

  // z.enum requires at least one value and a tuple type
  // In Zod v4, use message parameter instead of errorMap
  return z.enum(enumValues as [string, ...string[]], {
    message: `${field.display_name} must be one of: ${enumValues.join(', ')}`,
  })
}

/**
 * Evaluates a field condition against form data.
 * 
 * WHY: The SSE mandated conditional logic (e.g., "If Exploit Maturity = High,
 * require POC Link field"). This function evaluates those conditions.
 * 
 * @param condition - The condition to evaluate
 * @param data - Current form data
 * @returns True if condition is met
 */
export function evaluateCondition(condition: FieldCondition, data: Record<string, unknown>): boolean {
  const fieldValue = data[condition.field]

  switch (condition.operator) {
    case 'equals':
      return fieldValue === condition.value

    case 'not_equals':
      return fieldValue !== condition.value

    case 'contains':
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue.includes(condition.value)
      }
      return false

    case 'in':
      if (Array.isArray(condition.value)) {
        return condition.value.includes(fieldValue as string)
      }
      return false

    default:
      return false
  }
}

/**
 * Checks if a field should be visible based on its visible_if condition.
 */
export function isFieldVisible(field: SchemaField, data: Record<string, unknown>): boolean {
  if (!field.visible_if) {
    return true
  }
  return evaluateCondition(field.visible_if, data)
}

/**
 * Checks if a field should be required based on its required_if condition.
 */
export function isFieldRequired(field: SchemaField, data: Record<string, unknown>): boolean {
  if (field.required) {
    return true
  }
  if (!field.required_if) {
    return false
  }
  return evaluateCondition(field.required_if, data)
}

/**
 * Generates a Zod schema with conditional validation using superRefine.
 * 
 * WHY: Standard Zod schemas can't handle dynamic required fields.
 * We use superRefine to add conditional validation at runtime.
 * 
 * @param schema - MetadataIQ schema definition
 * @returns Zod schema with conditional validation
 */
export function generateConditionalZodSchema(
  schema: Schema
) {
  const baseShape: Record<string, ZodTypeAny> = {}

  // Generate base schema with all fields optional for conditional logic
  for (const field of schema.fields) {
    let fieldSchema = generateFieldSchema(field)
    
    // Make all fields optional in base schema; we validate conditionally
    if (field.visible_if || field.required_if) {
      fieldSchema = fieldSchema.optional()
    }
    
    baseShape[field.field_id] = fieldSchema
  }

  const baseSchema = z.object(baseShape)

  // Add conditional validation via superRefine
  return baseSchema.superRefine((data, ctx) => {
    for (const field of schema.fields) {
      const isVisible = isFieldVisible(field, data)
      const isRequired = isFieldRequired(field, data)
      const value = data[field.field_id]

      // Skip validation for hidden fields
      if (!isVisible) {
        continue
      }

      // Check required condition
      if (isRequired && (value === undefined || value === null || value === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${field.display_name} is required`,
          path: [field.field_id],
        })
      }
    }
  })
}

/**
 * Type helper for form data based on schema.
 * WHY: Provides TypeScript inference for dynamic forms.
 */
export type SchemaFormData = z.infer<ReturnType<typeof generateZodSchema>>
